/* SpawnAnything — in-game glue for crates + diamonds: balance in the top bar, lock state of selects / tool belt /
   presets, the crate screen as a paused overlay, battle rewards and the daily bonus.
   Rules live in src/game/unlocks.js (what is gated) and src/game/economy.js (numbers); state in src/game/inventory.js.
   Demo currency only: there is NO purchase flow and NO payment code (see buyDiamonds). */
import { browserStorage, createInventory } from '../game/inventory.js';
import { ECONOMY, awardBattle, awardMilestone, canGenerate, countBattle, chargeGeneration, claimDaily, generationQuota, openCrate } from '../game/economy.js';
import { KIND_NAME, LOOT, RARITY, gateOf, weaponAvailable } from '../game/unlocks.js';

const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const nf = new Intl.NumberFormat('de-DE');
const BOSSES = LOOT.filter(it => it.kind === 'unit' && RARITY[it.rarity].i >= 3);

/* ==================================================================================================
   STUB: Diamanten kaufen. Absichtlich KEIN Kaufablauf und KEIN Zahlungscode.
   Echte Käufe bräuchten Server-Konto, Zahlungsanbieter, Belege und eine serverseitige Kisten-Ziehung.
   ================================================================================================== */
export function buyDiamonds(toast = msg => alert(msg)) {
  showPacks(toast);
}

/* Diamond packs: PREVIEW ONLY. The buttons do not buy anything yet ("Bald verfügbar"). */
const PACK_CSS = `
.aw-packs{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;background:rgba(5,8,14,.72);padding:16px;font-family:var(--f-body,"Barlow Semi Condensed",system-ui,sans-serif);color:#eef2f8}
.aw-packs .box{background:#121925;border:1px solid rgba(205,220,255,.16);border-radius:12px;max-width:760px;width:100%;padding:18px;box-shadow:0 20px 60px rgba(0,0,0,.6)}
.aw-packs h2{margin:0 0 4px;font-family:var(--f-display,"Bungee",sans-serif);font-size:24px}
.aw-packs .sub{color:#9ba7b9;margin:0 0 14px;font-size:15px}
.aw-packs .row{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(190px,1fr))}
.aw-packs .pack{background:#1b2433;border:1px solid rgba(205,220,255,.14);border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:6px;align-items:flex-start}
.aw-packs .pack.best{border-color:#ffc93c}
.aw-packs .amt{font-family:var(--f-display,"Bungee",sans-serif);font-size:26px;display:flex;align-items:center;gap:8px}
.aw-packs .amt i{width:14px;height:14px;transform:rotate(45deg);background:linear-gradient(135deg,#9ff3ff,#3fb6ff);display:inline-block;border-radius:2px}
.aw-packs .fig{color:#9ba7b9;font-size:14px}
.aw-packs .bonus{background:#ffc93c;color:#1a1405;border-radius:4px;padding:1px 7px;font-size:12px;font-weight:700}
.aw-packs .buy{margin-top:auto;background:#ffc93c;color:#1a1405;border:0;border-radius:6px;padding:9px 14px;font:inherit;font-weight:800;cursor:pointer;width:100%}
.aw-packs .close{float:right;background:none;border:1px solid rgba(205,220,255,.2);color:#eef2f8;border-radius:6px;padding:4px 10px;cursor:pointer;font:inherit}
.aw-packs .note{color:#9ba7b9;font-size:13px;margin:12px 0 0}`;
export function showPacks(toast = () => {}) {
  if (document.querySelector('.aw-packs')) return;
  if (!document.getElementById('aw-packs-css')) { const st = document.createElement('style'); st.id = 'aw-packs-css'; st.textContent = PACK_CSS; document.head.appendChild(st); }
  const G = ECONOMY.generation, el = document.createElement('div');
  el.className = 'aw-packs'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); el.setAttribute('aria-label', 'Diamanten');
  el.innerHTML = `<div class="box"><button class="close" type="button" aria-label="Schließen">✕</button>
    <h2>Diamanten</h2><p class="sub">1 neue KI-Figur = ${G.cost} Diamanten. Figuren aus der Bibliothek und bereits erzeugte Figuren sind gratis.</p>
    <div class="row">${ECONOMY.packs.map((p, i) => `<div class="pack${i === 1 ? ' best' : ''}">
      <div class="amt"><i></i>${nf.format(p.diamonds)}</div>
      <div class="fig">${nf.format(p.figures)} Figuren</div>${p.bonus ? `<span class="bonus">${esc(p.bonus)}</span>` : ''}
      <button class="buy" type="button" data-id="${esc(p.id)}">${esc(p.price)}</button></div>`).join('')}</div>
    <p class="note">Kaufen ist noch nicht möglich – die Pakete sind eine Vorschau.</p></div>`;
  const close = () => { el.remove(); removeEventListener('keydown', onKey, true); };
  const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  el.addEventListener('click', e => {
    if (e.target === el || e.target.closest('.close')) close();
    else if (e.target.closest('.buy')) toast('Bald verfügbar');
  });
  addEventListener('keydown', onKey, true);
  document.body.appendChild(el);
  el.querySelector('.buy').focus();
}

/**
 * createShop({ toast, pauseGame, onChange, types, storage }) -> shop
 *   toast(msg)        game toast
 *   pauseGame()       pauses a running battle, returns a resume function (or null if nothing was paused)
 *   onChange()        content became (un)locked: rebuild selects, tool belt, …
 *   types             the live TYPES registry (to know each unit's standard weapon)
 */
export function createShop({ toast = () => {}, pauseGame = () => null, onChange = () => {}, types, storage = browserStorage() } = {}) {
  const inv = createInventory({ storage });
  let screen = null, loading = false, resume = null, pending = [];
  const presets = [];
  const $ = s => document.querySelector(s);

  /* ---------- top bar balance ---------- */
  let shown = inv.balance;
  function syncBalance() {
    const b = $('#diaBal'); if (b) b.textContent = nf.format(inv.balance);
    const btn = $('#crateBtn');
    if (btn) {
      btn.setAttribute('aria-label', `Kisten öffnen, ${nf.format(inv.balance)} Diamanten`);
      if (inv.balance > shown) { btn.classList.remove('bump'); void btn.offsetWidth; btn.classList.add('bump'); }
    }
    shown = inv.balance;
    if (screen) screen.balance = inv.balance;
  }
  inv.subscribe(syncBalance);
  $('#crateBtn')?.addEventListener('click', () => shop.open());

  /* ---------- lock state ---------- */
  const locked = (kind, key) => { const g = gateOf(kind, key); return g && !inv.isUnlocked(kind, key) ? g : null; };
  const weaponLocked = (weapon, typeKey) => weaponAvailable(inv, weapon, typeKey, types) ? null : gateOf('weapon', weapon);
  const rarityLabel = g => `${RARITY[g.rarity].name.replace('★ ', '')}`;

  /** <option>s for a select: free + unlocked first, crate-only ones grouped at the end with lock + rarity colour. */
  function options(kind, entries, typeKey) {
    const open = [], shut = [];
    for (const [k, v] of entries) {
      const g = kind === 'weapon' ? weaponLocked(k, typeKey) : locked(kind, k);
      if (!g) { open.push(`<option value="${esc(k)}">${esc(v.name)}</option>`); continue; }
      shut.push(`<option value="${esc(k)}" data-locked="${g.rarity}" style="color:${RARITY[g.rarity].color}">${esc(v.name)} · ${rarityLabel(g)}</option>`);
    }
    return open.join('') + (shut.length ? `<optgroup label="In Kisten (gesperrt)">${shut.join('')}</optgroup>` : '');
  }

  /** Picking a locked option reverts the select and opens the crate screen on that item.
      Must be installed BEFORE the game's own change listener (it stops the event). */
  function guardSelect(sel, kind, typeOf = () => null) {
    const remember = () => { sel.dataset.prev = sel.value; };
    sel.addEventListener('focus', remember);
    sel.addEventListener('pointerdown', remember);
    sel.addEventListener('change', e => {
      const k = sel.value, g = kind === 'weapon' ? weaponLocked(k, typeOf()) : locked(kind, k);
      if (!g) { remember(); return; }
      e.stopImmediatePropagation();
      sel.value = sel.dataset.prev && sel.dataset.prev !== k ? sel.dataset.prev : sel.querySelector('option:not([data-locked])')?.value || sel.value;
      toast(`${g.name} gibt es in Kisten (${rarityLabel(g)})`);
      shop.open(g);
    });
  }

  /** Tool belt: crate tools show a rarity stripe and "In Kisten" until unlocked; the Boss slot counts owned bosses. */
  function decorateTools() {
    for (const b of document.querySelectorAll('#tools [data-tool]')) {
      const g = gateOf('tool', b.dataset.tool); if (!g) continue;
      const isLocked = !inv.isUnlocked('tool', g.key);
      b.classList.add('crate'); b.classList.toggle('locked', isLocked); b.style.setProperty('--rc', RARITY[g.rarity].color);
      let sm = b.querySelector('small'); if (!sm) { sm = document.createElement('small'); b.appendChild(sm); }
      sm.textContent = isLocked ? 'In Kisten' : rarityLabel(g);
      b.title = isLocked ? `${g.name}: ${rarityLabel(g)}, gibt es in Kisten` : g.name;
    }
    const boss = $('#tools [data-crate="boss"]');
    if (boss) {
      const n = BOSSES.filter(it => inv.isUnlocked('unit', it.key)).length;
      boss.querySelector('small').textContent = `${n}/${BOSSES.length}`;
      boss.title = `Bosse aus Kisten: ${BOSSES.map(it => it.name).join(', ')}`;
    }
  }
  document.querySelectorAll('#tools [data-crate]').forEach(b => b.addEventListener('click', () => shop.open(b.dataset.crate === 'boss' ? BOSSES.find(it => !inv.isUnlocked('unit', it.key)) : null)));

  /** Preset chips with crate content stay playable as a trial ("Probe"). cfg = [type, count, weapon, …] per army. */
  function presetLocked(cfgs) { return cfgs.some(([type, , weapon]) => locked('unit', type) || weaponLocked(weapon, type)); }
  function markPreset(chip, cfgs) { presets.push([chip, cfgs]); paintPreset(chip, cfgs); }
  function paintPreset(chip, cfgs) {
    let tag = chip.querySelector('.probe');
    const on = presetLocked(cfgs);
    if (on && !tag) { tag = document.createElement('small'); tag.className = 'probe'; tag.textContent = 'Probe'; chip.appendChild(tag); }
    if (!on) tag?.remove();
    chip.title = on ? 'Enthält Inhalte aus Kisten: als Probeschlacht spielbar' : '';
  }

  function changed() { decorateTools(); for (const [c, cfg] of presets) paintPreset(c, cfg); onChange(); }

  /* ---------- crate screen (lazy, destroyed on close: no WebGL context stays behind) ---------- */
  const note = r => !r ? '' : r.isNew
    ? `Neu freigeschaltet${r.bonus ? ` · +${r.bonus} Diamanten Erstfund-Bonus` : ''}`
    : `Schon im Besitz (×${r.count})${r.bonus ? ` · +${r.bonus} Diamanten zurück` : ''}`;
  async function open(highlight = null) {
    if (screen || loading) return;
    loading = true; resume = pauseGame();
    try {
      const { createCrateScreen } = await import('./crates.js');
      screen = createCrateScreen(document.body, {
        balance: inv.balance, price: ECONOMY.cratePrice, eventChance: ECONOMY.supplyDropChance,
        onRoll(item) { pending.push({ item, res: openCrate(inv, item, ECONOMY.cratePrice) }); },
        onReward(item) { const p = pending.find(x => x.item === item && !x.shown); if (p) p.shown = true; changed(); return note(p?.res); },
        onBuy: () => buyDiamonds(msg => { toast(msg); screen?.el && flash(screen.el, msg); }),
        owned: it => inv.count(it.kind, it.key),
        onClose: close,
      });
      screen.show({ highlight });
    } catch (err) {
      console.error(err); toast('Kisten konnten nicht geladen werden.');
      screen = null; resume?.(); resume = null;
    } finally { loading = false; }
  }
  function close() {
    const s = screen; screen = null;
    s?.destroy();
    const fresh = pending.filter(p => p.res?.isNew).map(p => p.item.name);
    pending = [];
    changed();
    if (fresh.length) toast(`Freigeschaltet: ${fresh.join(', ')} – jetzt auswählbar`);
    resume?.(); resume = null;
  }
  // small in-overlay notice (the game toast sits below the crate overlay)
  function flash(host, msg) {
    const n = document.createElement('div'); n.className = 'aw-flash'; n.textContent = msg; n.setAttribute('role', 'status');
    n.style.cssText = 'position:absolute;left:50%;top:64px;transform:translateX(-50%);z-index:5;background:rgba(10,14,22,.9);border:1px solid rgba(255,201,60,.6);border-radius:6px;padding:8px 14px;font-weight:700';
    host.appendChild(n); setTimeout(() => n.remove(), 1800);
  }

  const shop = {
    inv,
    open,
    isOpen: () => !!screen || loading,
    locked, weaponLocked, options, guardSelect, markPreset, decorateTools,
    /** Sandbox / multiplayer battle finished: statistics only (no diamonds since 02.10.2026). */
    countBattle(won) { countBattle(inv, won); },
    /** One-time reward ('campaignStar' per new star, 'waveRecord' per new best wave); returns a German line or ''. */
    milestone(kind, steps) { const n = awardMilestone(inv, kind, steps); return n ? `+${n} Diamanten` : ''; },
    /** Battle finished: Blau is the player's side. Returns a short German line for the victory banner. */
    awardBattle({ won, enemies, seconds }) {
      const r = awardBattle(inv, { won, enemies, seconds });
      if (r.amount) return `+${r.amount} Diamanten${r.capped ? ' (Tageslimit erreicht)' : ''}`;
      return r.capped ? 'Tageslimit für Schlacht-Diamanten erreicht' : '';
    },
    /** Startup: welcome gift / daily bonus toast. */
    greet() {
      const d = claimDaily(inv);
      if (inv.fresh) toast(`Willkommen! ${nf.format(inv.balance)} Diamanten für deine ersten Kisten`);
      else if (d) toast(`Tagesbonus: +${d} Diamanten`);
    },
    kindName: k => KIND_NAME[k],
    /** New AI figures: quota(), can() before a request, charge() after a NEW model generation. */
    generation: { quota: () => generationQuota(inv), can: () => canGenerate(inv), charge: () => chargeGeneration(inv) },
    packs: () => showPacks(toast),
  };
  syncBalance(); decorateTools();
  return shop;
}
